/**
 * Phase 1.7 — Implements module index, v2.
 *
 * Cards are interactive and navigate to /implements/:id; the view
 * toggle (grid / list) actually re-renders.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cog, Edit2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import { EditEntityDialog } from '@/components/v2/dialogs/EditEntityDialog';
import { DeleteEntityDialog } from '@/components/v2/dialogs/DeleteEntityDialog';
import { EntityImage } from '@/components/EntityImage';

type ServiceStatus = 'in_service' | 'maintenance' | 'out_of_service' | 'retired';

interface ImplementRow {
  id: string;
  ranch_id: string;
  name: string;
  service_status?: ServiceStatus | null;
  asset_tag?: string | null;
  model_number?: string | null;
  serial_number?: string | null;
  hour_meter_value?: number | null;
  notes?: string | null;
  type?: string | null;
  condition?: string | null;
}

const STATUS_ORDER: ServiceStatus[] = ['in_service', 'maintenance', 'out_of_service', 'retired'];
const STATUS_LABEL: Record<ServiceStatus, string> = {
  in_service: 'In Service',
  maintenance: 'Maintenance',
  out_of_service: 'Out of Service',
  retired: 'Retired',
};
const STATUS_TONE: Record<ServiceStatus, string> = {
  in_service: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  maintenance: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
  out_of_service: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
  retired: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
};

const VIEW_KEY = 'implements';

const FIELDS = [
  { name: 'name', label: 'Name', required: true, placeholder: 'Hay baler' },
  {
    name: 'service_status',
    label: 'Service status',
    type: 'select' as const,
    half: true,
    defaultValue: 'in_service',
    options: STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  },
  { name: 'asset_tag', label: 'Asset tag', half: true, placeholder: 'IMP-001' },
  { name: 'model_number', label: 'Model #', half: true },
  { name: 'serial_number', label: 'Serial #', half: true },
  { name: 'hour_meter_value', label: 'Hour meter', type: 'number' as const, placeholder: '0' },
  { name: 'notes', label: 'Notes', type: 'textarea' as const },
];

const transformImplement = (v: Record<string, string>): Record<string, unknown> => ({
  ...(v.name !== undefined ? { name: v.name } : {}),
  ...(v.service_status !== undefined ? { service_status: v.service_status || 'in_service' } : {}),
  ...(v.asset_tag !== undefined ? { asset_tag: v.asset_tag || null } : {}),
  ...(v.model_number !== undefined ? { model_number: v.model_number || null } : {}),
  ...(v.serial_number !== undefined ? { serial_number: v.serial_number || null } : {}),
  ...(v.hour_meter_value !== undefined ? { hour_meter_value: v.hour_meter_value ? Number(v.hour_meter_value) : null } : {}),
  ...(v.notes !== undefined ? { notes: v.notes || null } : {}),
});

function statusOf(i: ImplementRow): ServiceStatus {
  return i.service_status ?? 'in_service';
}

export function ImplementsPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [items, setItems] = useState<ImplementRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | ServiceStatus>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<ImplementRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ImplementRow | null>(null);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Implements',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add implement
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: ImplementRow[] }>('/api/implements');
        if (!cancelled) setItems(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load implements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const filtered = useMemo<ImplementRow[]>(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (q && !`${i.name} ${i.asset_tag ?? ''} ${i.model_number ?? ''}`.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all' && statusOf(i) !== statusFilter) return false;
      return true;
    });
  }, [items, search, statusFilter]);

  const grouped = useMemo<Record<ServiceStatus, ImplementRow[]>>(() => {
    const out = STATUS_ORDER.reduce<Record<ServiceStatus, ImplementRow[]>>(
      (acc, s) => ({ ...acc, [s]: [] }),
      {} as Record<ServiceStatus, ImplementRow[]>,
    );
    for (const i of filtered) out[statusOf(i)].push(i);
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const inService = items.filter((i) => statusOf(i) === 'in_service').length;
    const maintenance = items.filter((i) => statusOf(i) === 'maintenance').length;
    return { inService, maintenance };
  }, [items]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || statusFilter !== 'all';
  const clear = (): void => { setSearch(''); setStatusFilter('all'); };
  const refresh = (): void => setRefreshTick((t) => t + 1);
  const stop = (e: React.MouseEvent): void => e.stopPropagation();

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Implement"
        endpoint="/api/implements"
        entityType="implement"
        fields={FIELDS}
        transform={transformImplement}
        onAdded={refresh}
      />
      {editTarget ? (
        <EditEntityDialog
          open={Boolean(editTarget)}
          onOpenChange={(o) => { if (!o) setEditTarget(null); }}
          title={`Edit ${editTarget.name}`}
          endpoint="/api/implements"
          entityId={editTarget.id}
          fields={FIELDS}
          initialValues={{
            name: editTarget.name,
            service_status: statusOf(editTarget),
            asset_tag: editTarget.asset_tag ?? '',
            model_number: editTarget.model_number ?? '',
            serial_number: editTarget.serial_number ?? '',
            hour_meter_value: editTarget.hour_meter_value != null ? String(editTarget.hour_meter_value) : '',
            notes: editTarget.notes ?? '',
          }}
          transform={transformImplement}
          onSaved={refresh}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteEntityDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
          endpoint="/api/implements"
          entityId={deleteTarget.id}
          entityName={deleteTarget.name}
          onDeleted={refresh}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard label="Implements" icon={Cog} primary={{ label: 'In service', value: totals.inService }} secondary={{ label: 'Maintenance', value: totals.maintenance, tone: totals.maintenance > 0 ? 'warning' : 'default' }} />
        <KpiCard label="Filter" primary={{ label: 'Showing', value: filtered.length }} secondary={{ label: 'Total', value: items.length }} />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search implements' }}
        filters={[
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | ServiceStatus)}
            options={[{ value: 'all', label: 'All statuses' }, ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Cog}
          title={hasActiveFilters ? 'No matches' : 'No implements yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Add an implement to track condition and assignments.'}
          action={hasActiveFilters ? <Button onClick={clear} variant="outline">Clear filters</Button> : <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" />Add implement</Button>}
        />
      ) : (
        STATUS_ORDER.filter((s) => grouped[s].length > 0).map((status) => (
          <SectionHeader key={status} title={STATUS_LABEL[status]} count={grouped[status].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[status].map((i) => (
                  <Card
                    key={i.id}
                    interactive
                    onClick={() => navigate(`/implements/${i.id}`)}
                    thumbnail={<EntityImage entityType="implement" entityId={i.id} alt={i.name} />}
                    title={i.name}
                    subtitle={[i.asset_tag, i.model_number].filter(Boolean).join(' \u00b7 ') || 'Implement'}
                    badge={
                      <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(i)]}`}>
                        {STATUS_LABEL[statusOf(i)]}
                      </span>
                    }
                    footer={
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setEditTarget(i); }} aria-label={`Edit ${i.name}`} className="h-7 w-7">
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setDeleteTarget(i); }} aria-label={`Archive ${i.name}`} className="h-7 w-7 text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                    <tr>
                      <Th>Name</Th>
                      <Th>Asset tag</Th>
                      <Th>Model</Th>
                      <Th>Service status</Th>
                      <Th className="text-right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[status].map((i) => (
                      <tr
                        key={i.id}
                        className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                        onClick={() => navigate(`/implements/${i.id}`)}
                      >
                        <Td className="font-medium">{i.name}</Td>
                        <Td className="font-mono text-xs">{i.asset_tag || '\u2014'}</Td>
                        <Td className="text-muted-foreground">{i.model_number || '\u2014'}</Td>
                        <Td>
                          <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(i)]}`}>
                            {STATUS_LABEL[statusOf(i)]}
                          </span>
                        </Td>
                        <Td className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setEditTarget(i); }} aria-label={`Edit ${i.name}`} className="h-7 w-7">
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setDeleteTarget(i); }} aria-label={`Archive ${i.name}`} className="h-7 w-7 text-destructive hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
